<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <div class="layout-column flex ma-2 elevation-5"
         style="border-radius:4px;position:relative;"
         v-bind:style="{background: compColor.contentFrame}">

      <ValidationObserver ref="InsuranceCompanyDetailForm" v-slot="{ valid, pristine, reset }" class="layout-column flex">
        <div v-bind:style="{'z-index': '4'}" class="layout-row layout-align-center-center pa-1">
          <div class="layout-row">
            <lx-btn small classes="mr-1"
                    @click="back"
                    icon="chevron_left"
                    :tooltip="$t('COMPONENT_INSURANCE_COMPANY.BUTTON.BACK')"
                    :hide-label="true">
            </lx-btn>
            <lx-btn small
                    classes="mr-1"
                    :disabled="pristine"
                    @click="clearForm(reset)"
                    icon="mdi-undo-variant"
                    :tooltip="$t('COMPONENT_INSURANCE_COMPANY.BUTTON.REVERT')"
                    hide-label></lx-btn>
            <lx-btn small
                    classes="mr-1"
                    :disabled="!valid || pristine"
                    @click="saveData"
                    icon="save"
                    :tooltip="insuranceCompanyId ? $t('COMPONENT_INSURANCE_COMPANY.BUTTON.SAVE_EXISTING') : $t('COMPONENT_INSURANCE_COMPANY.BUTTON.SAVE_NEW')"
                    hide-label></lx-btn>
          </div>
          <div class="flex layout-row"></div>
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="flex layout-column pa-2" style="overflow-y: auto;">
          <v-progress-linear :indeterminate="loading" v-if="loading" color="red accent-2"
                             background-color="red lighten-4" class="ma-0"
                             style="border-radius:0;height:4px;position: absolute;top:56px;left:0; width:100%;"></v-progress-linear>

          <lx-expansion-panels v-model="panel">
            <lx-expansion-panel disabled>
              <lx-expansion-panel-header icon="mdi-account-circle"
                                         disabled
                                         :main-headline="$t('COMPONENT_INSURANCE_COMPANY.HEADER.HEADLINE')"
                                         :sub-headline="$t('COMPONENT_INSURANCE_COMPANY.HEADER.SUB_HEADLINE')">
              </lx-expansion-panel-header>

              <lx-expansion-panel-content>
                <div class="pa-4 layout-row layout-wrap">
                  <div class="layout-row layout-wrap flex-100 mt-4">

                    <!-- name -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" ref="name" name="name"
                                           rules="required|min:2"
                                           v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          v-model="model.name"
                          :label="$t('COMPONENT_INSURANCE_COMPANY.FIELDS.NAME') + ' *'"
                          :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                          :error-messages="errors[0]"
                          :hide-details="!errors || errors.length <= 0"
                          :dense="denseFields"
                          outlined
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- ik -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" ref="ik" name="ik"
                                           rules="required|min:9|max:9|numeric"
                                           v-slot="{ errors }">
                        <lx-numberfield
                          :dark="compColor.isInDarkMode"
                          v-model="model.ik"
                          :hide-details="!errors || errors.length <= 0"
                          :label="$t('COMPONENT_INSURANCE_COMPANY.FIELDS.IK') + ' *'"
                          :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                          :error-messages="errors[0]"
                          :dense="denseFields"
                          outlined
                        ></lx-numberfield>
                      </validation-provider>
                    </div>

                    <!-- insurance_type -->

                    <div class="flex-gt-sm-25 flex-100 px-2">
                      <validation-provider tag="div" ref="insurance_type" name="insurance_type"
                                           rules="required"
                                           v-slot="{ errors }">
                        <v-select
                          name="type"
                          :color="compColor.primeColor"
                          v-model="model.insurance_type"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :dense="denseFields"
                          :label="$t('COMPONENT_INSURANCE_COMPANY.FIELDS.INSURANCE_TYPE') + ' *'"
                          :dark="compColor.isInDarkMode"
                          :items="insurance_types"
                          :menu-props="{ bottom: true, offsetY: true }">
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-on="on"
                                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                              <v-list-item-content class="py-0">
                                <label style="cursor:pointer;"
                                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                       class="lighten-4 text--darken-2">{{ item.text }}</label>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                      </validation-provider>
                    </div>

                  </div>

                  <div v-show="alertMsg" class="ml-2 flex-100">
                    <div class="layout-row wrap">
                      <div class="flex xs12">
                        <lx-alert-box color="red"
                                      class="layout wrap flex-gt-sm-100 flex-100 px-2 mt-1 py-1">
                          {{ alertMsg }}
                        </lx-alert-box>
                      </div>
                    </div>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>

          </lx-expansion-panels>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'

export default {
  name: 'InsuranceCompanyDetails',
  props: ['insuranceCompanyId', 'tabid'],
  data () {
    return {
      loading: false,
      model: {
        pending: true
      },
      insurance_types: [
        { value: 'private', text: this.$t('COMPONENT_INSURANCE_COMPANY.INSURANCE_TYPES.PRIVATE') },
        { value: 'public', text: this.$t('COMPONENT_INSURANCE_COMPANY.INSURANCE_TYPES.PUBLIC') }
      ],
      oldModel: {},
      panel: [0],
      error: {},
      alertMsg: ''
    }
  },
  created () {
    if (this.insuranceCompanyId) {
      this.getData()
    } else {
      this.oldModel = lodash.cloneDeep(this.model)
    }
  },
  methods: {
    /**
     * Reset form
     */
    clearForm (reset) {
      reset() // reset validation

      // reset alert
      this.alertMsg = null

      this.model = lodash.cloneDeep(this.oldModel)
    },

    back () {
      this.clearForm(this.$refs.InsuranceCompanyDetailForm.reset)

      // Trigger 'setShowDetails()' on parent
      this.$emit('set-show-details', false)
    },

    /**
     * Get data for insurance company with given id
     */
    getData () {
      if (this.loading || !this.insuranceCompanyId) return
      this.loading = true

      this.$http.get('/additionalInsuranceCompanies/' + this.insuranceCompanyId)
        .then(res => {
          this.model = res.data || []
          this.oldModel = lodash.cloneDeep(this.model)
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Save new insurance company
     */
    saveData () {
      if (this.loading) return
      this.loading = true

      // reset alert
      this.alertMsg = null

      this.$http.get('/additionalInsuranceCompanies/check/' + this.model.ik)
        .then(res => {
          if (res.data) {
            if (!res.data.valid && this.model.insurance_type !== 'private') {
              this.alertMsg = this.$t('COMPONENT_INSURANCE_COMPANY.ALERT.IK_INVALID')
              return
            }

            if (!this.insuranceCompanyId && (res.data.existDb || this.model.insurance_type !== 'private' && res.data.existMisd)) {
              this.alertMsg = this.$t('COMPONENT_INSURANCE_COMPANY.ALERT.IK_ALREADY_EXISTS')
              return
            }
            this.performSaveAction()
          }
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.loading = false
        })
    },

    performSaveAction () {
      // create new insurance company
      const data = {
        _id: this.insuranceCompanyId,
        name: this.model.name,
        ik: parseInt(this.model.ik),
        insurance_type: this.model.insurance_type
      }

      this.$http.post('/additionalInsuranceCompanies', data)
        .then(() => {
          this.NotifySystem(this.$t('COMPONENT_INSURANCE_COMPANY.SUCCESSFULLY_SAVED'))

          this.clearForm(this.$refs.InsuranceCompanyDetailForm.reset)
          this.back()
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
